import React, { useState } from "react";
import ImagePreviewModal from "../../helper-components/image-preview-modal";
import { toReceiptList } from "../../../utils/receipt-list";

/**
 * Payment History — every instalment recorded against the store.
 *
 * The rows come from the same /store/getById response the rest of the Payment
 * tab uses: its `payment` array. This used to render the latest instalment as a
 * column of disabled inputs, which could only ever show one of them.
 */

const headClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]";

const cellClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap";

const headings = [
  "#",
  "DATE",
  "METHOD",
  "TRANSACTION ID",
  "AMOUNT",
  "STATUS",
  "RECEIPT",
  "REMARKS",
];

// Payment rows have drifted in naming across the API, so each column reads the
// spellings that actually occur rather than assuming one.
const firstFilled = (...values) =>
  values.find((v) => v !== null && v !== undefined && v !== "");

// A receipt may sit under any of these; whichever is present is normalised to a
// list, so images uploaded from the mobile app appear against their own payment.
const receiptsOf = (row) =>
  toReceiptList(
    firstFilled(
      row?.payment_receipt,
      row?.payment_receipts,
      row?.receipt,
      row?.chalan_receipt
    )
  );

const formatDate = (raw) => {
  if (!raw) return "--";
  const d = new Date(raw);
  if (isNaN(d)) return String(raw);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "--";
  const n = Number(String(raw).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? String(raw) : n.toLocaleString();
};

const StorePaymentHistoryTab = ({ payments = [] }) => {
  const rows = Array.isArray(payments) ? payments : [];
  const [previewSrc, setPreviewSrc] = useState(null);

  return (
    <div className="w-full px-5 xl:px-10 mt-5">
      <div className="tab-container outline outline-[var(--list-table-border-color)] outline-1 border-radius overflow-auto">
        <table className="w-full border-collapse mt-0">
          <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
            <tr className="h-[50px]">
              {headings.map((h, i) => (
                <th key={i} className={headClass}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={headings.length}
                  className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
                >
                  No payments recorded yet.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row?.payment_id ?? row?.id ?? idx}
                  className="hover:bg-[var(--list-table-hover-bg)]"
                >
                  <td className={cellClass}>{idx + 1}</td>
                  <td className={cellClass}>
                    {formatDate(
                      firstFilled(
                        row?.payment_recevie_time,
                        row?.payment_date,
                        row?.created_date,
                        row?.created_at
                      )
                    )}
                  </td>
                  <td className={cellClass}>
                    {firstFilled(row?.payment_method) ?? "--"}
                  </td>
                  <td className={cellClass}>
                    {firstFilled(row?.transaction_id) ?? "--"}
                  </td>
                  <td
                    className={`${cellClass} tabular-nums`}
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatAmount(
                      firstFilled(
                        row?.payment_amount,
                        row?.amount,
                        row?.received_amount
                      )
                    )}
                  </td>
                  <td className={cellClass}>
                    {firstFilled(row?.payment_status) ?? "--"}
                  </td>
                  <td className={cellClass}>
                    {receiptsOf(row).length === 0 ? (
                      "--"
                    ) : (
                      <div className="flex items-center gap-2">
                        {receiptsOf(row).map((src, i) =>
                          String(src).toLowerCase().endsWith(".pdf") ? (
                            <a
                              key={i}
                              href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                            >
                              PDF
                            </a>
                          ) : (
                            <img
                              key={i}
                              alt={`Receipt ${i + 1}`}
                              src={src}
                              onClick={() => setPreviewSrc(src)}
                              className="rounded cursor-pointer bg-EBEBEB"
                              style={{
                                height: "2.25rem",
                                width: "2.25rem",
                                objectFit: "cover",
                              }}
                            />
                          )
                        )}
                      </div>
                    )}
                  </td>
                  <td className={cellClass}>
                    {firstFilled(
                      row?.payment_remarks,
                      row?.payment_table_remarks,
                      row?.remarks
                    ) ?? "--"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ImagePreviewModal
        isOpen={!!previewSrc}
        onClose={() => setPreviewSrc(null)}
        src={previewSrc}
      />
    </div>
  );
};

export default StorePaymentHistoryTab;
